import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Button, Card, Input, Label } from "@onemileauto/ui";
import { AuthApiError, requestPasswordReset } from "../../lib/auth-client";

/**
 * "Forgot your password?" — request a reset link. Always shows the same
 * success message regardless of whether the email matched an account (see
 * apps/mail's routes/auth.ts), following the anti-enumeration pattern
 * Cloudflare's own OTP login page uses — this page never has any way to
 * know (or reveal) whether the email it was given is real.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setSubmitted(true);
    } catch (err) {
      if (err instanceof AuthApiError && err.status === 429) {
        setError(err.message || "Too many requests. Please wait a while and try again.");
      } else {
        setError("Couldn't reach the mail server. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm p-6 sm:p-8">
        <Link to="/login" className="mb-4 flex w-fit items-center gap-1.5 text-sm font-medium text-muted hover:text-charcoal">
          <ArrowLeft size={16} />
          Back to sign in
        </Link>

        <h1 className="font-display text-2xl text-charcoal sm:text-3xl">Reset your password</h1>

        {submitted ? (
          <p className="mt-4 text-sm text-charcoal">
            If that email is registered with One Mile Auto Mail, we&rsquo;ve sent a link to reset your password. Check
            your inbox (and spam folder) — the link works once and expires in an hour.
          </p>
        ) : (
          <>
            <p className="mt-1.5 text-sm text-muted">
              Enter your email and we&rsquo;ll send you a link to set a new password.
            </p>
            <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              {error && (
                <p role="alert" className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2.5 text-sm text-danger">
                  {error}
                </p>
              )}

              <Button type="submit" size="lg" disabled={submitting} className="mt-1 w-full">
                {submitting ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
